import React from 'react';
import classNames from 'classnames';
import { RCThemeOverride } from '@reverbdotcom/cadence/components';

import LightboxImageItem from './lightbox_image_item';
import LightboxImageGrid from './LightboxImageGrid';
import {
  LightboxImageControls,
  LightboxImageThumbnails,
  LightboxIndexChangeSource,
  Swipeable,
  isLink,
  nextIndex,
  previousIndex,
  scrollThumbnailRow,
  useWindowArrowKeys,
} from './lightbox_image_parts';

interface Props {
  thumbURLs?: string[];
  imageURLs?: string[];
  imageAlts?: string[];
  fullsizeURLs: string[];
  index: number;
  onIndexChange: (index: number, details: { source: LightboxIndexChangeSource }) => void;
  // asks to open the full-screen viewer at the current index
  onOpenViewer: (details: { source: 'mainImage' | 'thumbnail' }) => void;
  isViewerOpen: boolean;
  withControls?: boolean;
  controlSize?: 'small' | 'medium';
  showAltsAsCaptions?: boolean;
  persistKeystrokes?: boolean;
  useAspectOverride?: boolean;
  disableFullScreen?: boolean;
  largerThumbs?: boolean;
  isImageGrid?: boolean;
  imageGridButton?: React.ReactNode;
  hideThumbs?: boolean;
  hidePrimary?: boolean;
  eagerLoadPrimaryImage?: boolean;
  disableImageOverlay?: boolean;
  // rendered inside the gallery, such as its viewer
  children?: React.ReactNode;
}

export default function LightboxImageGallery({
  thumbURLs,
  imageURLs,
  imageAlts,
  fullsizeURLs,
  index,
  onIndexChange,
  onOpenViewer,
  isViewerOpen,
  withControls,
  controlSize,
  showAltsAsCaptions,
  persistKeystrokes,
  useAspectOverride,
  disableFullScreen,
  largerThumbs,
  isImageGrid,
  imageGridButton,
  hideThumbs,
  hidePrimary,
  eagerLoadPrimaryImage,
  disableImageOverlay,
  children,
}: Props) {
  const [displayVideo, setDisplayVideo] = React.useState(false);
  const [aspectOverride, setAspectOverride] = React.useState<number>(null);
  const [primaryImageLoaded, setPrimaryImageLoaded] = React.useState(false);
  const [transitioning, setTransitioning] = React.useState(false);
  const thumbsRef = React.useRef<HTMLDivElement>(null);

  const isSingleImage = fullsizeURLs.length === 1;

  function shouldDisplayThumbnails() {
    if (isImageGrid || hideThumbs) return false;
    if (hidePrimary) return true;

    if (imageURLs) {
      return thumbURLs && thumbURLs.length > 1;
    }

    return thumbURLs && thumbURLs.length > 0;
  }

  const displayThumbnails = shouldDisplayThumbnails();

  React.useEffect(() => {
    if (!hidePrimary && displayThumbnails && !isViewerOpen) {
      scrollThumbnailRow(thumbsRef.current, index);
    }
  }, [hidePrimary, displayThumbnails, isViewerOpen, index]);

  function loadNext(e) {
    e.stopPropagation();
    setDisplayVideo(false);
    onIndexChange(nextIndex(index, fullsizeURLs.length), { source: 'next' });
  }

  function loadPrevious(e) {
    e.stopPropagation();
    setDisplayVideo(false);
    onIndexChange(previousIndex(index, fullsizeURLs.length), { source: 'previous' });
  }

  // the viewer takes over the arrow keys while it's open
  useWindowArrowKeys(persistKeystrokes && !isViewerOpen && !isSingleImage, {
    onPrevious: loadPrevious,
    onNext: loadNext,
  });

  function openViewer(source: 'mainImage' | 'thumbnail') {
    setDisplayVideo(false);
    onOpenViewer({ source });
  }

  function handleMainImageClick(evt, idx: number) {
    evt.preventDefault();
    if (isLink(fullsizeURLs[idx]) && !disableFullScreen) {
      openViewer('mainImage');
      return;
    }

    if (!isLink(fullsizeURLs[idx])) {
      setDisplayVideo(true);
    }
  }

  function handleThumbClick(evt, idx: number) {
    evt.preventDefault();
    setDisplayVideo(false);
    onIndexChange(idx, { source: 'thumbnail' });

    if (index === idx && !hidePrimary) return;

    if (imageURLs && !hidePrimary) {
      setTransitioning(true);
      setTimeout(() => setTransitioning(false), 50);
      return;
    }

    openViewer('thumbnail');
  }

  function handlePrimaryImageLoaded(evt) {
    if (!primaryImageLoaded && useAspectOverride) {
      setAspectOverride(evt.target.naturalHeight / evt.target.naturalWidth);
    }

    setPrimaryImageLoaded(true);
  }

  function renderPrimary() {
    return (
      <RCThemeOverride theme="light">
        <>
          <LightboxImageItem
            urlOrSlug={imageURLs[index]}
            index={index}
            alt={imageAlts?.[index]}
            onClick={handleMainImageClick}
            onLoad={handlePrimaryImageLoaded}
            controls={withControls && !isSingleImage &&
              <LightboxImageControls
                size={controlSize ?? 'small'}
                onPrevious={loadPrevious}
                onNext={loadNext}
              />
            }
            renderVideo={!isViewerOpen && displayVideo}
            aspectOverride={aspectOverride}
            eagerLoad={eagerLoadPrimaryImage}
            disableImageOverlay={disableImageOverlay}
          />
        </>
      </RCThemeOverride>
    );
  }

  function renderPrimarySwipeContainer() {
    if (!imageURLs) return null;
    if (hidePrimary) return null;
    if (isImageGrid) return null;

    if (isSingleImage || !displayThumbnails) {
      return (
        <div className="lightbox-image__primary">
          {renderPrimary()}
        </div>
      );
    }

    return (
      <div className="lightbox-image__primary">
        <Swipeable
          onSwipedLeft={loadNext}
          onSwipedRight={loadPrevious}
        >
          {renderPrimary()}
        </Swipeable>
      </div>
    );
  }

  function renderPrimaryCaption() {
    const alt = imageAlts?.[index];
    if (!showAltsAsCaptions || !alt) return null;

    return (
      <div
        className="lightbox-image__primary__caption"
      >
        {alt}
      </div>
    );
  }

  function renderGalleryGrid() {
    if (!isImageGrid) return null;

    return (
      <LightboxImageGrid
        imageUrls={imageURLs}
        imageAlts={imageURLs.map((_url, imageIndex) => imageAlts?.[imageIndex])}
        openLightbox={(e, imageIndex) => {
          handleThumbClick(e, imageIndex);
          handleMainImageClick(e, imageIndex);
        }}
        imageGridButton={imageGridButton}
      />
    );
  }

  function renderImageCache() {
    if (!primaryImageLoaded) return null;

    return (
      <div className="d-none">
        <div data-lightbox-preload>
          {imageURLs.filter(isLink).map((image, i) => (
            <img src={image} key={i} alt={imageAlts?.[i] || ''} />
          ))}
        </div>
      </div>
    );
  }

  const classes = classNames(
    'lightbox-image',
    { 'lightbox-image--single': isSingleImage },
    { 'lightbox-image--transitioning': transitioning },
    { 'lightbox-image--with-thumbnails': displayThumbnails },
    { 'lightbox-image--larger-thumbs': largerThumbs },
    { 'lightbox-image--hide-primary': hidePrimary },
    { 'lightbox-image--image-grid': isImageGrid },
  );

  return (
    <div className={classes}>
      {renderPrimarySwipeContainer()}
      {renderPrimaryCaption()}
      {renderGalleryGrid()}
      {displayThumbnails && thumbURLs &&
        <LightboxImageThumbnails
          ref={thumbsRef}
          urls={thumbURLs}
          alts={imageAlts}
          activeIndex={index}
          large={hidePrimary}
          disableImageOverlay={disableImageOverlay}
          onThumbClick={handleThumbClick}
        />
      }
      {renderImageCache()}
      {children}
    </div>
  );
}
